import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { contact } from '@/content/et/contact';
import { site } from '@/content/et/site';
import {
  matchEmergencyAction,
  setEmergencyClosed,
} from '@/lib/emergencyHours';
import { formatPhoneHref } from '@/lib/format';
import { submitNetlifyContact } from '@/lib/netlifyForm';
import { Button } from '@/components/ui/Button';
import { TextArea, TextField } from '@/components/ui/FormField';
import styles from './ContactForm.module.css';

type Status = 'idle' | 'submitting' | 'success' | 'error' | 'emergency';

interface Errors {
  name?: string;
  plate?: string;
  phone?: string;
  email?: string;
  message?: string;
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function ContactForm() {
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [botField, setBotField] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [emergencyNote, setEmergencyNote] = useState('');
  const [errors, setErrors] = useState<Errors>({});

  function validate(): boolean {
    const next: Errors = {};
    if (!name.trim()) next.name = site.form.required;
    if (!plate.trim()) next.plate = site.form.required;
    if (!phone.trim()) next.phone = site.form.required;
    if (!email.trim()) next.email = site.form.required;
    else if (!validateEmail(email.trim())) next.email = site.form.invalidEmail;
    if (!message.trim()) next.message = site.form.required;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validate()) return;
    if (botField) {
      setStatus('success');
      return;
    }

    const fields = {
      name: name.trim(),
      plate: plate.trim(),
      phone: phone.trim(),
      email: email.trim(),
      message: message.trim(),
    };

    // Emergency status switch (not a public lead)
    const emergency = matchEmergencyAction(fields);
    if (emergency) {
      setStatus('submitting');
      try {
        await setEmergencyClosed(emergency === 'close');
        setEmergencyNote(
          emergency === 'close'
            ? 'Erandkord salvestatud: täna kuvatakse kontaktis Suletud.'
            : 'Erandkord tühistatud: kehtib tavapärane lahtiolek.',
        );
        setStatus('emergency');
        setName('');
        setPlate('');
        setPhone('');
        setEmail('');
        setMessage('');
      } catch {
        setStatus('error');
      }
      return;
    }

    setStatus('submitting');
    try {
      await submitNetlifyContact({
        name: fields.name,
        plate: fields.plate.toUpperCase(),
        phone: fields.phone,
        email: fields.email,
        message: fields.message,
        'bot-field': '',
      });
      setStatus('success');
      setName('');
      setPlate('');
      setPhone('');
      setEmail('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'success' || status === 'emergency') {
    return (
      <div className={`${styles.status} ${styles.success}`} role="status">
        <p>
          {status === 'emergency' ? emergencyNote : site.form.success}
        </p>
        {status === 'success' ? (
          <p className={styles.weekend} style={{ marginTop: '0.5rem' }}>
            {contact.formWeekendNote}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <form
      name="contact"
      method="POST"
      data-netlify="true"
      netlify-honeypot="bot-field"
      action="/"
      className={styles.form}
      onSubmit={(e) => void onSubmit(e)}
      noValidate
    >
      <input type="hidden" name="form-name" value="contact" />
      <p className={styles.honeypot}>
        <label>
          Don’t fill this out:{' '}
          <input
            name="bot-field"
            value={botField}
            onChange={(e) => setBotField(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </p>

      <p className={styles.weekend}>{contact.formWeekendNote}</p>

      <div className={styles.row}>
        <TextField
          id="contact-name"
          name="name"
          label={site.form.name}
          required
          compact
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
          autoComplete="name"
        />
        <TextField
          id="contact-plate"
          name="plate"
          label={site.form.plate}
          required
          compact
          maxLength={20}
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          error={errors.plate}
          autoComplete="off"
        />
      </div>

      <div className={styles.row}>
        <TextField
          id="contact-phone"
          name="phone"
          type="tel"
          label={site.form.phone}
          required
          compact
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={errors.phone}
          autoComplete="tel"
        />
        <TextField
          id="contact-email"
          name="email"
          type="email"
          label={site.form.email}
          required
          compact
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          autoComplete="email"
        />
      </div>

      <TextArea
        id="contact-message"
        name="message"
        label={site.form.message}
        required
        compact
        rows={3}
        maxLength={2000}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        error={errors.message}
      />

      {status === 'error' ? (
        <div className={`${styles.status} ${styles.error}`} role="alert">
          {site.form.error}
          <a href={formatPhoneHref(contact.phoneE164)}>{contact.phoneDisplay}</a>
        </div>
      ) : null}

      <p className={styles.consent}>
        {site.form.consent.split('Vt privaatsuspoliitika')[0]}
        <Link to="/privaatsus">Vt privaatsuspoliitika</Link>.
      </p>

      <Button type="submit" disabled={status === 'submitting'} fullWidth>
        {status === 'submitting' ? 'Saadan…' : site.cta.send}
      </Button>
    </form>
  );
}
